import { useMemo } from 'react';

import { SubmitButton } from '@capsule/consumer-design-system';

import { Form, Formik } from 'formik';
import { useLocation } from 'react-router-dom';
import { css } from 'styled-components';

import { AutoCompleteInputWired } from 'src/app/components/Form/AutocompleteInput/container';
import { MedicationChip } from 'src/app/components/PageSections/DoctorTransferForm/MedicationsFieldArray/MedicationChip';
import {
  MedicationChipContainer,
  MedicationsChipListContainer,
} from 'src/app/components/PageSections/DoctorTransferForm/MedicationsFieldArray/MedicationChip/styles';
import { MedSearchItem } from 'src/app/components/PageSections/DoctorTransferForm/MedicationsFieldArray/styles';
import { useSearchMedication } from 'src/app/hooks/data/medication';
import type { TransferMedication } from 'src/app/hooks/data/prescriptionTransfers';
import { useCreateTransferTask } from 'src/app/hooks/data/prescriptionTransfers';
import {
  useLoggedInTransferFormData,
  useLoggedInTransferFormProgress,
} from 'src/app/hooks/feature_data/useLoggedInTransferFormData';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { AccountPageAddDependent } from 'src/app/router/paths';
import type { MedicationSearchResult } from 'src/app/types/medication';
import { FORM_INPUT_ICON_STRINGS } from 'src/consts';

import type { RenderedStepComponentProps } from '..';
import Step from '../Step';
import { STEP_NAME } from '../types';
import { getMedicationsForTransfer } from '../utils';

import { FamilyButton, FamilyButtonContainer } from './styles';

const MenuItem = ({ item }) => {
  const isEligible = item.eligibleForTransfer === true;

  return (
    <MedSearchItem ineligible={!isEligible}>
      {item.primaryName}
      {isEligible ? '' : ' — Unavailable'}
    </MedSearchItem>
  );
};

interface LoggedInTransferMedicationProps extends RenderedStepComponentProps {
  zipCode: string;
}

const searchMed = 'searchMed';
const initialValues = {
  medications: [],
  shouldTransferAllMeds: false,
  searchMed: '',
};

export const LoggedInTransferMedication = ({ handleNextClick, patients, zipCode }: LoggedInTransferMedicationProps) => {
  useSegmentEvent('viewTransferFormMedications', { shouldTrackOnMount: true });

  const { pathname } = useLocation();

  const { mutateAsync: searchMedication } = useSearchMedication();
  const dispatchTransferSuccessful = useSegmentEvent('transferSuccessful', { shouldTrackOnMount: false });
  const dispatchTransferFailed = useSegmentEvent('transferFailed', { shouldTrackOnMount: false });
  const { mutateAsync: createTransfer } = useCreateTransferTask();
  const { loggedInTransferFormData, setLoggedInTransferFormData } = useLoggedInTransferFormData();
  const { loggedInTransferFormProgress } = useLoggedInTransferFormProgress();

  // The type annotation here is rather stupid, but it satisfies the AutoCompleteInputWired interface
  const itemToString = (item: MedicationSearchResult) => {
    const typedItem = item;
    return typedItem?.primaryName;
  };

  // If user has not completed the doctor step, we will conclude it's the Pharmacy flow
  const isDoctorFlow = loggedInTransferFormProgress?.steps?.find(step => step === STEP_NAME.DOCTOR);
  // Determine copy based on flow and length of pharmacy/doctor name
  const title = useMemo(() => {
    if (isDoctorFlow) {
      const doctorOrPracticeName = loggedInTransferFormData?.doctorName;
      return doctorOrPracticeName && doctorOrPracticeName?.length < 21
        ? `Which medications should we get from ${doctorOrPracticeName}?`
        : 'Which medications should we get from your doctor?';
    } else {
      const pharmacyName = loggedInTransferFormData?.pharmacyName;
      return pharmacyName && pharmacyName?.length < 21
        ? `Which medications should we transfer from ${pharmacyName}?`
        : 'Which medications should we transfer from your pharmacy?';
    }
  }, [isDoctorFlow, loggedInTransferFormData?.doctorName, loggedInTransferFormData?.pharmacyName]);

  return (
    <Step title={title} text="We’ll contact them on your behalf">
      <Formik
        initialValues={{
          ...initialValues,
          ...(loggedInTransferFormData?.medications && {
            medications: loggedInTransferFormData.medications,
          }),
        }}
        onSubmit={async (values, { setSubmitting }) => {
          setSubmitting(true);
          const transferFormData = loggedInTransferFormData;
          if (!transferFormData) {
            return;
          }

          const transferPatientId = transferFormData.patientId;
          if (!transferPatientId) {
            return;
          }

          const medicationsToTransfer = getMedicationsForTransfer({
            ...transferFormData,
            medications: values.medications,
          });

          if (medicationsToTransfer.length === 0) {
            return;
          }

          await setLoggedInTransferFormData({
            ...transferFormData,
            medications: values.medications,
          });

          const transferIds = await createTransfer({
            patientId: transferPatientId,
            transfers: medicationsToTransfer,
          });

          const medicationNames = medicationsToTransfer.map((med: TransferMedication) => med.medicationName);

          const isPrimaryPatient = loggedInTransferFormData?.patientIsPrimary;

          if (transferIds) {
            dispatchTransferSuccessful({
              page: pathname,
              type: 'Manual',
              medication_names: medicationNames,
              account_holder: isPrimaryPatient ? 'Primary' : 'Dependent',
            });
          } else {
            dispatchTransferFailed({
              page: pathname,
              error_code: '...',
              error_summary: ' ...',
            });
          }

          if (isPrimaryPatient) {
            window.analytics.identify({ has_ever_transferred: true });
          }
          handleNextClick();
        }}
      >
        {({ values, setValues, setFieldValue, isSubmitting }) => {
          return (
            <Form>
              <AutoCompleteInputWired
                showLoader={true}
                id={searchMed}
                name={searchMed}
                aria-label={'Search a medication name'}
                placeholder={`example: "Sertraline" or "Zoloft"`}
                searchHelperText={'Search a medication name'}
                renderMenuItem={MenuItem}
                itemToString={itemToString}
                dispatchSegmentTrack={() => undefined}
                fetchItemsOnChange={async input => {
                  if (input.length > 2) {
                    const medications = await searchMedication({
                      zipCode,
                      searchTerm: input,
                    });
                    return medications;
                  }
                }}
                onSelectedItemChangeCallback={item => {
                  const medications = values.medications ? [...values.medications, item] : [item];
                  setValues({
                    ...values,
                    searchMed: '',
                    medications: medications as MedicationSearchResult[],
                  });
                  setLoggedInTransferFormData({
                    ...loggedInTransferFormData,
                    medications: medications as MedicationSearchResult[],
                  });
                }}
                iconString={FORM_INPUT_ICON_STRINGS.system_search}
                disabled={false}
                debounceWaitTime={200}
                isTransferForm={true}
              />

              <MedicationsChipListContainer>
                {values.medications
                  .filter(med => !!med)
                  .map(med => {
                    const primaryName = med?.primaryName;

                    return (
                      <MedicationChipContainer key={primaryName}>
                        <MedicationChip
                          title={primaryName}
                          onClick={() => {
                            const medications = values.medications.filter(item => item.primaryName !== primaryName);
                            setFieldValue('medications', medications);
                            setLoggedInTransferFormData({
                              ...loggedInTransferFormData,
                              medications: medications,
                            });
                          }}
                        />
                      </MedicationChipContainer>
                    );
                  })}
              </MedicationsChipListContainer>
              <SubmitButton
                variant="contained-secondary-lg"
                isSubmitting={isSubmitting}
                css={css`
                  margin-top: ${({ theme }) => theme.space.s7};
                  width: 100%;
                  max-width: 24rem;

                  ${({ theme }) => theme.mediaQueries.smallDown} {
                    max-width: 100%;
                  }
                `}
              >
                Submit
              </SubmitButton>
            </Form>
          );
        }}
      </Formik>
      {patients && patients.length === 1 && (
        <FamilyButtonContainer>
          <>Ordering for someone else?</>
          <FamilyButton to={AccountPageAddDependent.toPath()} size="medium" style="tertiary" block>
            + Add A Family Member
          </FamilyButton>
        </FamilyButtonContainer>
      )}
    </Step>
  );
};
